import axios from "axios";
import {
    createUserWithEmailAndPassword,
    onAuthStateChanged,
    sendPasswordResetEmail,
    signInWithEmailAndPassword,
    signOut
} from "firebase/auth";
import {
    arrayUnion,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    query,
    setDoc,
    updateDoc,
    where
} from "firebase/firestore";
import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { env } from "../env/env";
import { auth, db } from "../firebase";

const UserContext = createContext();

// User data cache key for localStorage
const USER_CACHE_KEY = 'wcb_user_cache';

// Helper functions for user cache management
const getUserFromCache = () => {
  try {
    const cached = localStorage.getItem(USER_CACHE_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch (error) {
    console.error('Error loading user cache:', error);
    return {};
  }
};

const saveUserToCache = (userData) => {
  try {
    if (userData && Object.keys(userData).length > 0) {
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(userData));
    }
  } catch (error) {
    console.error('Error saving user cache:', error);
  }
};

const clearUserCache = () => {
  try {
    localStorage.removeItem(USER_CACHE_KEY);
  } catch (error) {
    console.error('Error clearing user cache:', error);
  }
};

export const AuthContextProvider = ({ children }) => {
  // Initialize user state from cache for instant display
  const [user, setUserInternal] = useState(getUserFromCache());

  // Wrapper for setUser that also saves to cache
  // Using useCallback to maintain stable reference across renders
  const setUser = useCallback((userData) => {
    setUserInternal(userData);
    saveUserToCache(userData);
  }, []);

  const [resetOrderState, setResetOrderState] = useState(false);
  const [save, setSave] = useState(0);
  const [currentOrderId, setCurrentOrderId] = useState("");
  const [orderStoreName, setOrderStoreName] = useState("");
  const [scanDisabled, setScanDisabled] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [receiptData, setReceiptData] = useState(null);
  const [overallData, setOverallData] = useState(null);
  const [receiptImage, setReceiptImage] = useState({ img: null, currency: "" });
  const [distanceStoreAddress, setDistanceStoreAddress] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [country, setCountry] = useState("");
  const [coords, setCoords] = useState({
    lat: localStorage.getItem("latitude") || null,
    lng: localStorage.getItem("longitude") || null,
  });
  const [showModal, setShowModal] = useState(false);
  const [editAddress, setEditAddress] = useState(false);
  const [orderData, setOrderData] = useState([]);
  const [stripePromise, setStripePromise] = useState(null);
  const [receiptLink, setReceiptLink] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [couponsLoaded, setCouponsLoaded] = useState(false);

  const { i18n } = useTranslation();
  const [language, setLanguageInternal] = useState(i18n.language);

  const setLanguage = (language) => {
    // The useTranslation hook stores language state already,
    // but the point of adding setters and getters here is because
    // i18n.changeLanguage() does not force a render while the React context
    // hook does.
    // So stale data is displayed in some cases (mainly in Profile.jsx) without
    // adding getters/setters here.
    i18n.changeLanguage(language);
    setLanguageInternal(language);
  };

  const sendVerificationEmail = async (email, id) => {
    const loginEmail = email.toLowerCase();

    // Use the new backend endpoint for sending verification emails
    // This endpoint uses Firebase Admin SDK to generate the verification link
    // and sends it via email using the email service
    // Note: The backend only needs the email address, not the user ID
    const backendUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/firebase/send-verify-account-email`;
    const requestBody = {
      email: loginEmail,
      production: env.production
    };

    const response = await fetch(backendUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(requestBody)
    });

    // Check if the request was successful, throw error if not
    // This allows the calling code to handle errors appropriately
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to send verification email (${response.status})`);
    }

    return await response.json();
  };

  const reloadUser = async () => {
    if (user) {
      const q = query(collection(db, "users"), where("id", "==", user.id));
      const queryResult = await getDocs(q);
      if (queryResult.docs.length > 0) {
        const userData = queryResult.docs[0].data();
        setUser(userData);
      }
    }
  };

  const verifyEmail = async (email, id) => {
    const loginEmail = email.toLowerCase();

    const q = query(collection(db, "users"), where("email", "==", loginEmail));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.docs.length === 0) {
      throw new Error("unknown-account");
    }
    const userSnapshot = querySnapshot.docs[0];

    let userData = userSnapshot.data();
    userData = { ...userData, verified: true };

    if (userData.id !== id) {
      throw new Error("Sorry. Invalid verification link.");
    }

    const docRef = doc(db, "users", id);
    await setDoc(docRef, userData);
    return true;
  };

  const registerUser = async (email, password, name, referralCode) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;
      const userID = user.uid;
      const docRef = doc(db, "users", userID);

      // await sendEmailVerification(auth.currentUser);

      // New email template sent via backend
      // Also sends admin notification email via SendGrid (handled in backend)
      const backendUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/firebase/send-verify-account-email`;
      const requestBody = {
        "email": email,
        "name": name, // Pass name so backend can send admin notification
        "production": env.production
      };

      const backendResponse = await fetch(backendUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(requestBody)
      });

      const backendResponseData = await backendResponse.json();

      if (!backendResponse.ok) {
        console.error('Failed to send verification email:', backendResponse.status);
      }

      const payload = {
        address: [],
        email: email,
        name: name,
        id: userID,
        uid: userID
      };
      await setDoc(docRef, payload);

      if (referralCode) {
        const codesRef = collection(db, "referralCodes");
        const codeDocs = await getDocs(
          query(codesRef, where("code", "==", referralCode))
        );
        if (codeDocs.docs.length === 0) {
          return userID;
        }
        const referrerID = codeDocs.docs[0].id;

        const referralDocRef = doc(db, "referrals", userID);
        await setDoc(referralDocRef, {
          referrer: referrerID,
          hasMadePurchase: false,
          isDiscountActiveForReferrer: false,
          isDiscountActiveForReferred: false
        });
      }

      return userID;
    } catch (error) {
      console.error("Error registering user:", error);
      throw error; // Rethrow the error to handle it in the calling code
    }
  };

  const deleteDocument = async (collectionName, documentId) => {
    try {
      const docRef = doc(db, collectionName, documentId);
      await deleteDoc(docRef);
      await reloadUser();
      console.log("Document deleted successfully.");
    } catch (error) {
      console.error("Error deleting document:", error);
    }
  };

  const deleteAddress = async (addressData, addressId) => {
    const updatedAddress = addressData.filter(
      (_, index) => index !== addressId,
    );
    try {
      const userDocRef = doc(db, "users", user.id); // Assuming 'users' is the collection name
      await updateDoc(userDocRef, { address: updatedAddress });
      console.log("Address deleted successfully!");
      // Optionally, you can reload the data or update the state here
    } catch (error) {
      console.error("Error deleting address:", error);
      // Handle the error as needed
    }
  };

  const sendPasswordEmail = async (email) => {
    sendPasswordResetEmail(email);
  };

  const logout = async () => {
    await signOut(auth);
    setUser({});
    clearUserCache();
    setShowShoppingServiceMenu(false);
    localStorage.removeItem("overallOrder");
    localStorage.removeItem("order");
    // Clear any saved redirect URL to prevent redirect loops after logout
    sessionStorage.removeItem("redirectAfterLogin");
  };

  const loginUser = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    if (result.user.emailVerified) {
      const q = query(
        collection(db, "users"),
        where("id", "==", result.user.uid),
      );
      const querySnapshot = await getDocs(q);

      // Check if user document exists in Firestore
      // For users created through store-dashboard, the document might not exist in web-app's Firestore
      if (querySnapshot.docs.length === 0) {
        // Create a basic user document for users that don't have one (e.g., created through store-dashboard)
        const docRef = doc(db, "users", result.user.uid);
        const newUserData = {
          address: [],
          email: result.user.email,
          name: "", // Will be empty until user updates their profile
          id: result.user.uid,
          uid: result.user.uid
        };
        await setDoc(docRef, newUserData);
        setUser(newUserData);
      } else {
        const userData = querySnapshot.docs[0].data();
        setUser(userData);
      }
    } else {
      await logout();
      throw new Error("unverified");
    }
  };

  const getCouponLimit = (coupons, couponCode) => {
    // Find the coupon object that matches the provided coupon code
    const matchedCoupon = coupons.find((coupon) => coupon.name === couponCode);

    if (matchedCoupon) {
      return matchedCoupon.limit;
    } else {
      return;
    }
  };

  const saveAppliedCoupon = async (
    userId,
    currentUser,
    couponCode,
    coupons,
  ) => {
    // Only save a coupon code if it exists and is not blank.
    if (couponCode && couponCode !== "") {
      try {
        const userRef = doc(db, "users", userId);

        if (currentUser) {
          const appliedCoupons = currentUser.appliedCoupons || [];
          const matchedCouponIndex = appliedCoupons.findIndex(
            (item) => item.coupon === couponCode,
          );

          if (matchedCouponIndex !== -1) {
            const appliedCount =
              appliedCoupons[matchedCouponIndex].appliedCount;
            const limit = getCouponLimit(coupons, couponCode); // Replace with a function to get the limit for the applied coupon

            // Check if the applied count is less than the limit or if there is no limit
            if (appliedCount < limit || limit === -100) {
              const updatedCount = appliedCount + 1;
              appliedCoupons[matchedCouponIndex].appliedCount = updatedCount;
              await updateDoc(userRef, { appliedCoupons });
            } else {
              throw new Error("Coupon limit exceeded");
            }
          } else {
            const newCoupon = { coupon: couponCode, appliedCount: 1 };
            await updateDoc(userRef, { appliedCoupons: arrayUnion(newCoupon) });
          }
        }
      } catch (error) {
        console.error("Error updating coupon data:", error);
      }
    }
  };

  const markReferrerPurchase = async (userID) => {
    const docRef = doc(db, "referrals", userID);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      if (docSnap.data().hasMadePurchase) {
        await updateDoc(docRef, {
          isDiscountActiveForReferred: false
        });
      } else {
        await updateDoc(docRef, {
          hasMadePurchase: true,
          isDiscountActiveForReferrer: true,
          isDiscountActiveForReferred: true
        });

        // Send emails notifying customers of free delivery

        const referrerID = docSnap.data().referrer;

        const referrerDocRef = doc(db, "users", referrerID);
        const friendDocRef = doc(db, "users", userID);

        const [referrerData, friendData] = await Promise.all(
          [referrerDocRef, friendDocRef].map(ref => getDoc(ref).then(snap => snap.data()))
        );

        const requestBody = {
          referrerName: referrerData.name,
          friendName: friendData.name,
        };

        await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/v1/refer/email-fd-referrer`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ ...requestBody, email: referrerData.email }),
          },
        );
        await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/v1/refer/email-fd-referred`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ ...requestBody, email: friendData.email }),
          },
        );
      }
    }

    const querySnapshot = await getDocs(
      query(
        collection(db, "referrals"),
        where("referrer", "==", userID),
        where("isDiscountActiveForReferrer", "==", true)
      ),
    );
    const docSnap2 = querySnapshot.docs[0];
    if (docSnap2 !== undefined && docSnap2.exists()) {
      const docRef2 = doc(db, "referrals", docSnap2.id);
      await updateDoc(docRef2, {
        isDiscountActiveForReferrer: false
      });
    }
  }

  const updateUserAddress = async (addressIndex, wholeData, addressData) => {
    const updatedAddress = wholeData.filter(
      (_, index) => index !== addressIndex,
    );

    // Remove undefined fields from addressData to prevent Firebase errors
    // Firebase doesn't accept undefined values, only null or omitted fields
    const cleanedAddressData = Object.fromEntries(
      Object.entries(addressData).filter(([_, value]) => value !== undefined)
    );

    updatedAddress.push(cleanedAddressData);
    try {
      const userDocRef = doc(db, "users", user.id);
      await updateDoc(userDocRef, { address: updatedAddress });
      await reloadUser();
      console.log("Address updated successfully!");
    } catch (error) {
      console.error("Error updating address:", error);
    }
  };

  function toRadians(degrees) {
    return degrees * (Math.PI / 180);
  }

  function distanceBetween(coord1, coord2) {
    const [lat1, lon1] = coord1;
    const [lat2, lon2] = coord2;

    const R = 6371;

    const lat1Rad = toRadians(lat1);
    const lon1Rad = toRadians(lon1);
    const lat2Rad = toRadians(lat2);
    const lon2Rad = toRadians(lon2);

    const dLat = lat2Rad - lat1Rad;
    const dLon = lon2Rad - lon1Rad;

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(dLon / 2) ** 2;

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = R * c;

    return distance.toFixed(2);
  }

  const location = useLocation();
  const navigate = useNavigate();

  const [hasAuthLoaded, setHasAuthLoaded] = useState(false);

  // Track last redirect to prevent infinite redirect loops when using browser back button
  const lastRedirectUrlRef = useRef(null);
  const redirectTimeoutRef = useRef(null);

  useEffect(() => {
    const loadOrderData = async () => {
      const q = query(collection(db, "orders"), where("user", "==", user.id));
      const querySnapshot = await getDocs(q);
      const orderArray = [];
      querySnapshot.forEach((doc) =>
        orderArray.push({ id: doc.id, ...doc.data() }),
      );
      setOrderData(orderArray);
    };

    // Only load orders after Firebase Auth has finished loading AND user is authenticated
    if (hasAuthLoaded && user && user.id) {
      loadOrderData();
    }
  }, [user, hasAuthLoaded]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && user.emailVerified) {
        const q = query(collection(db, "users"), where("id", "==", user.uid));
        const queryResult = await getDocs(q);
        if (queryResult.docs.length > 0) {
          const userData = queryResult.docs[0].data();
          setUser(userData);
          setHasAuthLoaded(true);
          
          // Check for preserved redirect URL and navigate there, otherwise show shopping service menu
          const redirectUrl = sessionStorage.getItem("redirectAfterLogin");
          if (redirectUrl) {
            sessionStorage.removeItem("redirectAfterLogin");
            navigate(redirectUrl);
          } else {
            setShowShoppingServiceMenu(true);
          }
        } else {
          // Create a basic user document for users that don't have one (e.g., created through store-dashboard)
          const docRef = doc(db, "users", user.uid);
          const newUserData = {
            address: [],
            email: user.email,
            name: "", // Will be empty until user updates their profile
            id: user.uid,
            uid: user.uid
          };
          await setDoc(docRef, newUserData);
          setUser(newUserData);
          setHasAuthLoaded(true);
          
          // Check for preserved redirect URL and navigate there, otherwise show shopping service menu
          const redirectUrl = sessionStorage.getItem("redirectAfterLogin");
          if (redirectUrl) {
            sessionStorage.removeItem("redirectAfterLogin");
            navigate(redirectUrl);
          } else {
            setShowShoppingServiceMenu(true);
          }
        }
      } else {
        // User is not authenticated, clear cache and user state
        setUser({});
        clearUserCache();
        setHasAuthLoaded(true);

        // Routes the user is allowed to be in when not logged in.
        const authorisedRoutes = [
          "/", //login
          "/register",
          "/data-contact",
          "/faq",
          "/terms-and-conditions",
          "/privacy-policy",
          "/forgot-password",
        ];

        // More routes the user is allowed to be in when not logged in,
        // but these routes take query parameters
        // so detection logic needs to be different.
        const partialAuthorisedRoutes = [
          "/reset",
          "/waiting-verification",
          "/verify",
          // "/pss-checkout",
          // "/pss-success",
        ];

        const currentPath = location.pathname;
        const isInAuthorisedRoutes = authorisedRoutes.some(
          (routePath) => routePath === currentPath,
        );
        const isInPartialAuthorisedRoutes = partialAuthorisedRoutes.some(
          (routePath) => currentPath.includes(routePath),
        );
        const isAuthosied = isInAuthorisedRoutes || isInPartialAuthorisedRoutes;
        if (!isAuthosied) {
          const fullUrl = location.pathname + location.search;

          // Detect and break infinite redirect loops when using browser back button
          // If we just redirected from this same URL, use a more forceful redirect
          if (lastRedirectUrlRef.current === fullUrl) {
            // Use window.location.replace for a forceful redirect that clears history
            // This breaks the loop and prevents access to protected routes
            window.location.replace("/");
            return;
          }

          // Track this redirect
          lastRedirectUrlRef.current = fullUrl;

          // Clear the tracking after a short delay to allow future legitimate redirects
          if (redirectTimeoutRef.current) {
            clearTimeout(redirectTimeoutRef.current);
          }
          redirectTimeoutRef.current = setTimeout(() => {
            lastRedirectUrlRef.current = null;
          }, 1000);

          // Preserve the full URL (path + query params) for redirect after login
          sessionStorage.setItem("redirectAfterLogin", fullUrl);
          // Use replace: true to prevent infinite redirect loops when using browser back button
          navigate("/", { replace: true });
        }
      }
    });

    return () => {
      unsubscribe();
      // Clean up redirect timeout on unmount
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, [location, navigate]);

  useEffect(() => {
    const fetchMyData = () => {
      if (user && user.id) {
        try {
          onSnapshot(
            query(collection(db, "orders"), where("user", "==", user.id)),
            (docs) => {
              const order = docs.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              }));
              setOrderData(order);
            },
            (error) => {
              console.error("Error fetching orders:", error);
            },
          );
        } catch (error) {
          console.error("Error in fetching orders:", error);
        }
      }
    };

    // Only fetch orders after Firebase Auth has finished loading AND user is authenticated
    if (hasAuthLoaded && user && user.id) {
      fetchMyData();
    }
  }, [user, hasAuthLoaded]);

  const unPaidOrder = orderData.find((order) => !order.isPaid);
  useEffect(() => {
    if (unPaidOrder) {
      setScanDisabled(true);
      setCurrentOrderId(unPaidOrder.id);
    }
  }, [orderData, resetOrderState, unPaidOrder]);

  const sendReceiptEmail = async (receipt) => {
    console.log("RECEIPT:", receipt);
    try {
      // Send receipt data to our backend so it's emailed to customer
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/customer/email-receipt`,
        receipt,
      );
    } catch (err) {
      console.log("emailErr", err);
    }
  };

  const loadReceipt = async (receiptData) => {
    try {
      // Send receipt data to an invoice generator site as well
      const response = await fetch(
        "https://invoice-generator-pinb.onrender.com/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(receiptData),
        },
      );

      if (response.ok) {
        const responseData = await response.json();
        setReceiptLink(responseData.url);
        return responseData.url;
      } else {
        console.error("Error:", response.status);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const genReceipt = (
    customerName,
    orderID,
    serviceType,
    servicePrice,
    discountPrice,
    storeName,
    currency,
    address,
  ) => {
    return {
      from: "CarryBags Ltd.",
      logo: "https://i.imgur.com/smmVOeC.png",
      balance_title: "Total Paid",
      notes_title: "Store",
      to_title: "Customer",
      fields: {
        discounts: true,
      },
      to: `${customerName}`,
      number: `${orderID}`,
      discounts: discountPrice,
      ship_to: `${customerName}\n${address}`,
      items: [
        {
          name: `${serviceType}`,
          quantity: 1,
          unit_cost: servicePrice,
        },
      ],
      currency: `${currency}`,
      notes: `${storeName}\n\n\n\nThank you!`,
    };
  };

  const [currentBags, setCurrentBags] = useState([]);
  const [currentStore, setCurrentStore] = useState(null);
  const [, setUsedBagID] = useState("");

  const [successData, setSuccessData] = useState(null);
  const [pssSuccessData, setPssSuccessData] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const priceString = urlParams.get('priceString');
    return priceString ? { priceString } : null;
  });

  const lastScannedBagString = useRef("");

  const addBag = async (
    bagString,
    notifyAdd,
    notifyAlreadyScanned,
    notifyInvalid,
    notifyBagUsed,
    notifyAddedFromAnotherStore,
    goNext,
  ) => {
    // To avoid duplicate notifications, check if we've already scanned the code
    // a few moments ago
    if (lastScannedBagString.current === bagString) {
      return;
    }

    // Keep track of last QR code scanned, for a short time
    lastScannedBagString.current = bagString;
    setTimeout(() => {
      lastScannedBagString.current = "";
    }, 5000);

    try {
      const newBag = JSON.parse(bagString);

      // Check parsed JSON fits schema.
      if (newBag.bagID && newBag.storeID) {
        const currentBagID = newBag.bagID;
        const bagRef = doc(db, "bags", currentBagID);
        const bagSnap = await getDoc(bagRef);
        if (bagSnap.exists()) {
          const bagData = bagSnap.data();
          // If this is reached, then the bag was already used to pay for a previous order.
          if (bagData.orderID) {
            setUsedBagID((oldBagID) => {
              if (oldBagID === currentBagID) {
                return currentBagID;
              } else {
                notifyBagUsed();
                return currentBagID;
              }
            });
            return;
          }
        }
        const currentStoreID = newBag.storeID;
        setCurrentBags((bags) => {
          // Check if bag is already scanned.
          const isAlreadyScanned = bags.some(
            (bag) => currentBagID === bag.bagID,
          );

          // If bag was scanned before, return old bags.
          if (isAlreadyScanned) {
            notifyAlreadyScanned();
            // goNext(bags);
            return bags;
          }
          // If newly scanned bag has different store ID,
          // remove bags from previous store and add only this bag.
          else if (bags.length > 0 && bags[0].storeID !== currentStoreID) {
            notifyAddedFromAnotherStore();
            const result = [newBag];
            goNext(result);
            return result;
          }

          // Create new array containing new bags and set its state.
          notifyAdd();
          const result = [...bags, newBag];
          goNext(result);
          return result;
        });
      } else {
        notifyInvalid();
      }
    } catch (err) {
      console.log(err);
      notifyInvalid();
    }
  };

  const loadCoupons = async () => {
    const querySnapshot = await getDocs(collection(db, "Coupons"));
    const coupons = querySnapshot.docs.map((doc) => doc.data());
    setCoupons(coupons);
    setCouponsLoaded(true);
  };

  useEffect(() => {
    // Only load coupons after Firebase Auth has finished loading AND user is authenticated
    // Coupons collection requires authentication (Firebase rules: allow read: if request.auth != null)
    if (hasAuthLoaded && !couponsLoaded && user && user.id) {
      loadCoupons();
    }
  }, [hasAuthLoaded, couponsLoaded, user]);

  const loadStoreWith = async (currentBags) => {
    // Get store ID from bags array,
    // assuming no logistics errors occurred
    // and that all bags were sent to correct store.
    const storeID = currentBags[0].storeID;

    // Load store document from Firebase.

    const q = query(collection(db, "stores"), where("uid", "==", storeID));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.docs.length > 0) {
      const storeDoc = querySnapshot.docs[0];
      const storeData = { ...storeDoc.data(), id: storeDoc.id };
      setCurrentStore(storeData);
      return storeData;
    }

    const docRef = doc(db, "stores", storeID);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const storeData = { ...docSnap.data(), id: docSnap.id };
      setCurrentStore(storeData);
      return storeData;
    }

    throw new Error(`could-not-load-store: ${storeID}`);
  };

  const saveScanPageWith = async (currentBags) => {
    const store = await loadStoreWith(currentBags);
    await loadCoupons();
    const paymentStage = {
      currentBags: currentBags,
      store: store,
    };
    localStorage.setItem("order", JSON.stringify(paymentStage));
  };

  const sendOrderEmailToAdmin = async (orderID) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/email-order`,
        {
          orderID: orderID,
          production: env.production,
        },
      );
    } catch (err) {
      console.log(err);
    }
  };

  const deleteOrderData = () => {
    setCurrentBags([]);
    setCurrentStore(null);
    setReceiptData(null);
    setOverallData(null);
    localStorage.removeItem("order");
    localStorage.removeItem("overallData");
  };

  const setBagsAsUsed = async (currentBags, orderID) => {
    const promiseArray = currentBags.map((bag) => {
      const bagRef = doc(db, "bags", bag.bagID);
      return setDoc(bagRef, {
        ...bag,
        orderID: orderID,
      });
    });
    await Promise.all(promiseArray);
  };

  const getStoreById = async (storeId) => {
    const myDocRef = doc(db, "stores", storeId);
    const myDocSnap = await getDoc(myDocRef);
    if (myDocSnap.exists()) {
      return myDocSnap.data();
    } else {
      const q = query(collection(db, "stores"), where("uid", "==", storeId));
      const querySnapshot = await getDocs(q);
      if (querySnapshot.docs.length > 0) {
        return querySnapshot.docs[0].data();
      }
    }
  };

  // Side-effecting function that sets bag and store data
  // if localStorage has this data, returning true if the data is there.
  // If the data is not there, intialises bag and store data to the empty state,
  // and returns false.
  const hasOrderInLocalStorage = () => {
    // Check if there is order data in localStorage
    const orderString = localStorage.getItem("order");
    if (orderString) {
      try {
        const order = JSON.parse(orderString);
        const hasOrderInLocalStorage =
          order && order.currentBags && order.currentBags.length && order.store;
        if (hasOrderInLocalStorage) {
          setCurrentBags(order.currentBags);
          setCurrentStore(order.store);
          return true;
        } else {
          setCurrentBags([]);
          setCurrentStore(null);
          return false;
        }
      } catch (err) {
        console.log("orderStr", err);
        setCurrentBags([]);
        setCurrentStore(null);
        return false;
      }
    }
  };

  const [showShoppingServiceMenu, setShowShoppingServiceMenu] = useState(false);

  const [lastActiveService, setLastActiveService] = useState(
    () => localStorage.getItem("lastActiveService") || "handsfree"
  );

  const updateLastActiveService = (service) => {
    setLastActiveService(service);
    localStorage.setItem("lastActiveService", service);
  };

  return (
    <UserContext.Provider
      value={{
        registerUser,
        user,
        setUser,
        logout,
        loginUser,
        selectedCategories,
        setSelectedCategories,
        sendPasswordEmail,
        save,
        setSave,
        coords,
        setCoords,
        country,
        setCountry,
        distanceStoreAddress,
        setDistanceStoreAddress,
        distanceBetween,
        clientSecret,
        setClientSecret,
        overallData,
        setOverallData,
        saveAppliedCoupon,
        markReferrerPurchase,
        scanDisabled,
        setScanDisabled,
        currentOrderId,
        setCurrentOrderId,
        receiptData,
        setReceiptData,
        deleteDocument,
        showModal,
        setShowModal,
        deleteAddress,
        editAddress,
        setEditAddress,
        updateUserAddress,
        orderStoreName,
        setOrderStoreName,
        receiptImage,
        setReceiptImage,
        orderData,
        setOrderData,
        setResetOrderState,
        resetOrderState,
        language,
        setLanguage,
        stripePromise,
        setStripePromise,
        sendReceiptEmail,
        receiptLink,
        setReceiptLink,
        loadReceipt,
        genReceipt,
        sendVerificationEmail,
        verifyEmail,
        reloadUser,
        currentBags,
        setCurrentBags,
        addBag,
        currentStore,
        setCurrentStore,
        saveScanPageWith,
        coupons,
        sendOrderEmailToAdmin,
        deleteOrderData,
        successData,
        setSuccessData,
        pssSuccessData,
        setPssSuccessData,
        setBagsAsUsed,
        getStoreById,
        hasOrderInLocalStorage,
        hasAuthLoaded,
        setHasAuthLoaded,
        showShoppingServiceMenu,
        setShowShoppingServiceMenu,
        lastActiveService,
        updateLastActiveService,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const UserAuth = () => {
  return useContext(UserContext);
};
